import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../lib/auth';
import { Button } from '../components/Button';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  // 신규 사용자가 /login 에 오는 건 거의 가입 의도. 기본을 가입으로.
  // ?mode=login 이면 로그인 모드로 진입.
  const [mode, setMode] = useState<'login' | 'signup'>(
    params.get('mode') === 'login' ? 'login' : 'signup',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await api('/api/auth/signup', {
          method: 'POST',
          json: { email, password, name, tenantName },
        });
        await login(email, password);
      }
      const dest = (loc.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/app';
      navigate(dest);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main" className="min-h-dvh grid place-items-center px-4 py-16">
      <div className="w-full max-w-md glass rounded-3xl p-8">
        <Link to="/" className="flex items-center gap-2 font-display font-bold">
          <span className="grid place-items-center h-8 w-8 rounded-lg bg-brand-600 text-white">
            <i className="fa-solid fa-cube" aria-hidden="true" />
          </span>
          빌드업
        </Link>
        <h1 className="mt-6 font-display text-2xl font-bold">
          {mode === 'login' ? '로그인' : '14일 무료 시작'}
        </h1>
        <p className="text-sm mt-1 text-surface-900/70 dark:text-surface-100/70">
          {mode === 'login'
            ? '인테리어 운영을 다시 한 곳에서.'
            : '신용카드 없이 가입하세요.'}
        </p>
        <form className="mt-6 space-y-3" onSubmit={onSubmit}>
          {mode === 'signup' && (
            <>
              <Field
                id="tenantName"
                label="회사명"
                value={tenantName}
                onChange={setTenantName}
                icon="fa-solid fa-building"
                required
              />
              <Field
                id="name"
                label="이름"
                value={name}
                onChange={setName}
                icon="fa-solid fa-user"
                required
              />
            </>
          )}
          <Field
            id="email"
            type="email"
            autoComplete="email"
            label="이메일"
            value={email}
            onChange={setEmail}
            icon="fa-solid fa-envelope"
            required
          />
          <Field
            id="password"
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            label="비밀번호 (8자 이상)"
            value={password}
            onChange={setPassword}
            icon="fa-solid fa-lock"
            required
            minLength={8}
          />
          {err && (
            <p role="alert" className="text-sm text-red-600">
              {err}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={busy} icon="fa-solid fa-arrow-right">
            {busy ? '처리 중…' : mode === 'login' ? '로그인' : '계정 만들기'}
          </Button>
        </form>
        <button
          type="button"
          className="mt-4 text-sm text-brand-600 hover:underline"
          onClick={() => setMode((m) => (m === 'login' ? 'signup' : 'login'))}
        >
          {mode === 'login' ? '아직 계정이 없으신가요? 가입하기' : '이미 계정이 있으신가요? 로그인'}
        </button>
      </div>
    </main>
  );
}

function Field(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  icon?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
}) {
  return (
    <label htmlFor={props.id} className="block">
      <span className="text-xs font-medium">{props.label}</span>
      <span className="mt-1 flex items-center gap-2 rounded-xl border border-surface-200 dark:border-white/10 bg-white/60 dark:bg-white/[0.04] px-3 py-2 focus-within:border-brand-500">
        {props.icon && <i className={`${props.icon} text-surface-900/60 dark:text-surface-100/60`} aria-hidden="true" />}
        <input
          id={props.id}
          type={props.type ?? 'text'}
          required={props.required}
          minLength={props.minLength}
          autoComplete={props.autoComplete}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          className="flex-1 bg-transparent outline-none"
        />
      </span>
    </label>
  );
}
